/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import numelDimension from '@stdlib/ndarray/base/numel-dimension';
import getStride from '@stdlib/ndarray/base/stride';
import getOffset from '@stdlib/ndarray/base/offset';
import getData from '@stdlib/ndarray/base/data-buffer';
import ndarraylike2scalar from '@stdlib/ndarray/base/ndarraylike2scalar';
import { ndarray as strided } from './../../../../strided/covarmtk';


// MAIN //

/**
* Computes the covariance of two one-dimensional ndarrays provided known means and using a one-pass textbook algorithm.
*
* ## Notes
*
* -   The function expects the following ndarrays:
*
*     -   first one-dimensional input ndarray.
*     -   second one-dimensional input ndarray.
*     -   a zero-dimensional ndarray specifying the degrees of freedom adjustment. Setting this parameter to a value other than `0` has the effect of adjusting the divisor during the calculation of the covariance according to `N-c` where `c` corresponds to the provided degrees of freedom adjustment and `N` corresponds to the number of elements in each input ndarray. When computing the population covariance, setting this parameter to `0` is the standard choice (i.e., the provided arrays contain data constituting entire populations). When computing the unbiased sample covariance, setting this parameter to `1` is the standard choice (i.e., the provided arrays contain data sampled from larger populations; this is commonly referred to as Bessel's correction).
*     -   a zero-dimensional ndarray specifying the mean of the first one-dimensional ndarray.
*     -   a zero-dimensional ndarray specifying the mean of the second one-dimensional ndarray.
*
* @param {ArrayLikeObject<Object>} arrays - array-like object containing ndarrays
* @returns {number} covariance
*
* @example
* import vector from '@stdlib/ndarray/vector/ctor';
* import scalar2ndarray from '@stdlib/ndarray/from-scalar';
*
* var opts = {
*     'dtype': 'generic'
* };
*
* var x = vector( [ 1.0, -2.0, 2.0 ], opts.dtype );
* var y = vector( [ 2.0, -2.0, 1.0 ], opts.dtype );
*
* var correction = scalar2ndarray( 1.0, opts );
* var meanx = scalar2ndarray( 1.0/3.0, opts );
* var meany = scalar2ndarray( 1.0/3.0, opts );
*
* var v = covarmtk( [ x, y, correction, meanx, meany ] );
* // returns ~3.8333
*/
function covarmtk( arrays ) {
	var correction;
	var meanx;
	var meany;
	var x;
	var y;

	x = arrays[ 0 ];
	y = arrays[ 1 ];

	correction = ndarraylike2scalar( arrays[ 2 ] );
	meanx = ndarraylike2scalar( arrays[ 3 ] );
	meany = ndarraylike2scalar( arrays[ 4 ] );

	return strided( numelDimension( x, 0 ), correction, meanx, getData( x ), getStride( x, 0 ), getOffset( x ), meany, getData( y ), getStride( y, 0 ), getOffset( y ) ); // eslint-disable-line max-len
}


// EXPORTS //

export default covarmtk;
